import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import NavigationHeader from '../homepage/components/NavigationHeader';
import PermitTypeCard from './components/PermitTypeCard';
import AvailabilityCalendar from './components/AvailabilityCalendar';
import ItineraryRecommendation from './components/ItineraryRecommendation';
import SafetyChecklist from './components/SafetyChecklist';
import WeatherForecast from './components/WeatherForecast';
import OfflineResourceCard from './components/OfflineResourceCard';
import BookingForm from './components/BookingForm';
import BookingSummary from './components/BookingSummary';

const VisitorPlanningHub = () => {
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeTab, setActiveTab] = useState('permits');
  const [showBookingForm, setShowBookingForm] = useState(false);

  const permitTypes = [
  {
    id: 'day-pass',
    name: 'Day Pass',
    duration: 'Valid for 1 day',
    description: 'Perfect for first-time visitors wanting to explore the main trails and visitor center. Includes guided orientation and access to primary viewing areas.',
    icon: 'Sun',
    price: { php: '₱500', usd: '$10' },
    features: [
    'Access to main trails and viewing platforms',
    'Visitor center orientation session',
    'Basic trail map and safety briefing',
    'Photography permitted in designated areas'],

    popular: true,
    quota: 45
  },
  {
    id: 'multi-day',
    name: 'Multi-Day Trek',
    duration: 'Valid for 3 days',
    description: 'Immersive experience for serious nature enthusiasts. Includes camping permits, ranger-guided treks, and access to restricted conservation zones.',
    icon: 'Mountain',
    price: { php: '₱2,500', usd: '$50' },
    features: [
    'All Day Pass benefits included',
    'Camping permit for designated sites',
    'Ranger-guided trek to restricted zones',
    'Wildlife monitoring participation opportunity',
    'Emergency communication device rental'],

    popular: false,
    quota: 12
  },
  {
    id: 'research',
    name: 'Research Access',
    duration: 'Valid for 7 days',
    description: 'Specialized permit for academic researchers and conservation professionals. Requires institutional affiliation and research proposal approval.',
    icon: 'Microscope',
    price: { php: '₱5,000', usd: '$100' },
    features: [
    'Extended access to all zones',
    'Data collection equipment permitted',
    'Collaboration with park scientists',
    'Access to research facilities',
    'Specimen collection (with approval)'],

    popular: false,
    quota: 8
  },
  {
    id: 'volunteer',
    name: 'Volunteer Program',
    duration: 'Valid for 14 days',
    description: 'Join conservation efforts through hands-on volunteer work. Includes accommodation, meals, and meaningful contribution to park management.',
    icon: 'Heart',
    price: { php: '₱8,000', usd: '$160' },
    features: [
    'Dormitory accommodation included',
    'Three meals per day provided',
    'Conservation training workshops',
    'Trail maintenance participation',
    'Community engagement activities',
    'Volunteer certificate upon completion'],

    popular: true,
    quota: 20
  }];


  const itineraries = [];


  const offlineResources = [
  {
    id: 'trail-map',
    name: 'Offline Trail Maps',
    type: 'map',
    description: 'Detailed topographic maps with GPS coordinates, trail markers, and emergency waypoints',
    format: 'PDF + GPX',
    size: '45 MB',
    lastUpdated: 'Dec 1, 2025',
    essential: true
  },
  {
    id: 'species-guide',
    name: 'Species Identification Guide',
    type: 'guide',
    description: 'Comprehensive field guide with photos and descriptions of 200+ endemic species',
    format: 'PDF',
    size: '120 MB',
    lastUpdated: 'Nov 15, 2025',
    essential: false
  },
  {
    id: 'safety-protocols',
    name: 'Emergency Safety Protocols',
    type: 'emergency',
    description: 'Critical safety information, emergency contacts, and first aid procedures',
    format: 'PDF',
    size: '8 MB',
    lastUpdated: 'Dec 5, 2025',
    essential: true
  },
  {
    id: 'packing-checklist',
    name: 'Complete Packing Checklist',
    type: 'checklist',
    description: 'Season-specific packing lists with gear recommendations and prohibited items',
    format: 'PDF',
    size: '2 MB',
    lastUpdated: 'Nov 20, 2025',
    essential: true
  }];


  const handlePermitSelect = (permitId) => {
    const permit = permitTypes?.find((p) => p?.id === permitId);
    setSelectedPermit(permit);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleItinerarySelect = (itineraryId) => {
    console.log('Selected itinerary:', itineraryId);
  };

  const handleBookingSubmit = (formData) => {
    console.log('Booking submitted:', formData);
    alert('Booking submitted successfully! Redirecting to payment...');
  };

  const handleProceedToBooking = () => {
    if (!selectedPermit) {
      alert('Please select a permit type first');
      return;
    }
    if (!selectedDate) {
      alert('Please select a visit date first');
      return;
    }
    setShowBookingForm(true);
    setActiveTab('booking');
  };

  const tabs = [
  { id: 'permits', label: 'Permit Types', icon: 'Ticket' },
  { id: 'calendar', label: 'Availability', icon: 'Calendar' },
  { id: 'itineraries', label: 'Itineraries', icon: 'Map' },
  { id: 'safety', label: 'Safety & Weather', icon: 'Shield' },
  { id: 'resources', label: 'Offline Resources', icon: 'Download' },
  { id: 'booking', label: 'Complete Booking', icon: 'FileText' }];


  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {/* Navigation Header */}
      <NavigationHeader />
      
      {/* Add padding to account for fixed header */}
      <div className="pt-20">
        {/* Hero Section */}
        <div className="relative h-[400px] overflow-hidden">
          <Image
            src="https://images.unsplash.com/photo-1692204192163-b5b6c2f9f3da"
            alt="Aerial view of pristine Puerto Princesa Subterranean River National Park showing emerald green forest canopy meeting turquoise ocean waters with limestone karst formations"
            className="w-full h-full object-cover" />

          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[var(--color-background)]/50 to-[var(--color-background)]" />
          
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <div className="inline-flex items-center gap-2 bg-[var(--color-accent)] text-[var(--color-accent-foreground)] px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Icon name="Award" size={16} />
                UNESCO World Heritage Site
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-[var(--color-foreground)] mb-4">
                Plan Your Visit
              </h1>
              <p className="text-xl text-[var(--color-muted-foreground)] max-w-2xl mx-auto">
                Comprehensive trip planning with offline capabilities and safety integration
              </p>
            </div>
          </div>
        </div>
        {/* Navigation Breadcrumb */}
        <div className="border-b border-[var(--color-border)] bg-[var(--color-card)]">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors">
                Home
              </Link>
              <Icon name="ChevronRight" size={14} color="var(--color-muted-foreground)" />
              <span className="text-[var(--color-foreground)] font-medium">Visitor Planning Hub</span>
            </div>
          </div>
        </div>
        {/* Tab Navigation */}
        <div className="border-b border-[var(--color-border)] bg-[var(--color-card)] sticky top-20 z-40">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex overflow-x-auto">
              {tabs?.map((tab) =>
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-6 py-4 border-b-2 transition-all whitespace-nowrap ${
                activeTab === tab?.id ?
                'border-[var(--color-accent)] text-[var(--color-accent)]' :
                'border-transparent text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'}`
                }>

                  <Icon name={tab?.icon} size={18} />
                  <span className="font-medium">{tab?.label}</span>
                </button>
              )}
            </div>
          </div>
        </div>
        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Permits Tab */}
          {activeTab === 'permits' &&
          <div className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
                  Choose Your Permit Type
                </h2>
                <p className="text-[var(--color-muted-foreground)] max-w-2xl mx-auto">
                  Select the permit that best matches your visit duration and conservation goals. All fees support habitat protection and local community development.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {permitTypes?.map((permit) =>
              <PermitTypeCard
                key={permit?.id}
                permit={permit}
                onSelect={handlePermitSelect}
                isSelected={selectedPermit?.id === permit?.id} />

              )}
              </div>

              {selectedPermit &&
            <div className="flex justify-center">
                  <Button variant="default" size="lg" onClick={() => setActiveTab('calendar')}>
                    Continue to Date Selection
                    <Icon name="ArrowRight" size={20} />
                  </Button>
                </div>
            }
          </div>
          }

          {/* Calendar Tab */}
          {activeTab === 'calendar' &&
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <AvailabilityCalendar
                onDateSelect={handleDateSelect}
                selectedDate={selectedDate} />

              </div>
              <div>
                <BookingSummary
                selectedPermit={selectedPermit}
                selectedDate={selectedDate}
                groupSize={1} />

                {selectedPermit && selectedDate &&
              <Button
                variant="default"
                size="lg"
                className="w-full mt-4"
                onClick={handleProceedToBooking}>

                    Proceed to Booking
                    <Icon name="ArrowRight" size={20} />
                  </Button>
                }
              </div>
            </div>
          }

          {/* Itineraries Tab */}
          {activeTab === 'itineraries' &&
          <div className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-[var(--color-foreground)] mb-3">
                  Recommended Itineraries
                </h2>
                <p className="text-[var(--color-muted-foreground)] max-w-2xl mx-auto">
                  Expert-curated routes designed for different fitness levels and conservation interests. Each itinerary includes ranger guidance and safety protocols.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {itineraries?.map((itinerary) =>
              <ItineraryRecommendation
                key={itinerary?.id}
                itinerary={itinerary}
                onSelect={handleItinerarySelect} />

              )}
              </div>
            </div>
          }

          {/* Safety & Weather Tab */}
          {activeTab === 'safety' &&
          <div className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-[var(--color-foreground)] mb-3">
                  Safety Preparation & Weather
                </h2>
                <p className="text-[var(--color-muted-foreground)] max-w-2xl mx-auto">
                  Essential safety information and real-time weather forecasts to ensure a secure and enjoyable visit.
                </p>
              </div>

              <WeatherForecast />
              <SafetyChecklist />
            </div>
          }

          {/* Offline Resources Tab */}
          {activeTab === 'resources' &&
          <div className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-[var(--color-foreground)] mb-3">
                  Offline Resources
                </h2>
                <p className="text-[var(--color-muted-foreground)] max-w-2xl mx-auto">
                  Download essential materials for offline access during your visit. All resources are regularly updated with latest conservation data.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {offlineResources?.map((resource) =>
              <OfflineResourceCard key={resource?.id} resource={resource} />
              )}
              </div>

              <div className="bg-[var(--color-info)] bg-opacity-10 border border-[var(--color-info)] rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <Icon name="Smartphone" size={24} color="var(--color-info)" />
                  <div>
                    <h3 className="font-semibold text-[var(--color-info)] mb-2">Progressive Web App</h3>
                    <p className="text-sm text-[var(--color-foreground)] mb-3">
                      Install our PWA for reliable offline access to all resources, even in areas with limited connectivity.
                    </p>
                    <Button variant="outline" size="sm">
                      <Icon name="Download" size={16} />
                      Install PWA
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          }

          {/* Booking Tab */}
          {activeTab === 'booking' &&
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                {showBookingForm ?
              <BookingForm
                selectedPermit={selectedPermit}
                selectedDate={selectedDate}
                onSubmit={handleBookingSubmit} /> :


              <div className="bg-[var(--color-card)] rounded-lg p-12 border border-[var(--color-border)] text-center">
                    <Icon name="AlertCircle" size={48} color="var(--color-warning)" className="mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-[var(--color-foreground)] mb-2">
                      Complete Previous Steps
                    </h3>
                    <p className="text-[var(--color-muted-foreground)] mb-6">
                      Please select a permit type and visit date before proceeding to booking.
                    </p>
                    <div className="flex gap-3 justify-center">
                      <Button variant="outline" onClick={() => setActiveTab('permits')}>
                        Select Permit
                      </Button>
                      <Button variant="outline" onClick={() => setActiveTab('calendar')}>
                        Choose Date
                      </Button>
                    </div>
                  </div>
              }
              </div>
              <div>
                <BookingSummary
                selectedPermit={selectedPermit}
                selectedDate={selectedDate}
                groupSize={1} />

              </div>
            </div>
          }
        </div>
        {/* Footer CTA */}
        <div className="border-t border-[var(--color-border)] bg-[var(--color-card)] mt-12">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-xl font-semibold text-[var(--color-foreground)] mb-2">
                  Need Assistance?
                </h3>
                <p className="text-[var(--color-muted-foreground)]">
                  Our visitor services team is here to help plan your perfect conservation experience.
                </p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" size="lg">
                  <Icon name="Phone" size={20} />
                  Contact Support
                </Button>
                <Button variant="default" size="lg">
                  <Icon name="MessageCircle" size={20} />
                  Live Chat
                </Button>
              </div>
            </div>
          </div>
        </div>
        {/* Trust Signals Footer */}
        <div className="bg-[var(--color-background)] border-t border-[var(--color-border)]">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <Icon name="Shield" size={32} color="var(--color-success)" className="mx-auto mb-2" />
                <p className="text-sm font-medium text-[var(--color-foreground)]">Secure Booking</p>
                <p className="text-xs text-[var(--color-muted-foreground)]">SSL Encrypted</p>
              </div>
              <div>
                <Icon name="Award" size={32} color="var(--color-accent)" className="mx-auto mb-2" />
                <p className="text-sm font-medium text-[var(--color-foreground)]">UNESCO Site</p>
                <p className="text-xs text-[var(--color-muted-foreground)]">World Heritage</p>
              </div>
              <div>
                <Icon name="RefreshCw" size={32} color="var(--color-info)" className="mx-auto mb-2" />
                <p className="text-sm font-medium text-[var(--color-foreground)]">Free Cancellation</p>
                <p className="text-xs text-[var(--color-muted-foreground)]">Up to 48 hours</p>
              </div>
              <div>
                <Icon name="Users" size={32} color="var(--color-primary)" className="mx-auto mb-2" />
                <p className="text-sm font-medium text-[var(--color-foreground)]">Expert Guides</p>
                <p className="text-xs text-[var(--color-muted-foreground)]">Certified Rangers</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitorPlanningHub;